import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

export type ReceivePurchaseOrderConfirmPayload = {
  received_date: string;
  received_quantity: number | null;
};

export type ReceivePurchaseOrderDialogProps = {
  open: boolean;
  title: string;
  busy?: boolean;
  error?: string | null;
  showQuantity: boolean;
  orderedQty?: number;
  alreadyReceived?: number;
  pendingQty?: number;
  lineCount?: number;
  onClose: () => void;
  onConfirm: (payload: ReceivePurchaseOrderConfirmPayload) => void;
};

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function appDateLocale(language: string | undefined): string {
  return (language || 'es').toLowerCase().startsWith('it') ? 'it-IT' : 'es-ES';
}

function formatIsoDateForLocale(iso: string, locale: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatQty(value: number | undefined, locale: string): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(locale, { maximumFractionDigits: 2 });
}

export function ReceivePurchaseOrderDialog({
  open,
  title,
  busy = false,
  error = null,
  showQuantity,
  orderedQty,
  alreadyReceived,
  pendingQty,
  lineCount,
  onClose,
  onConfirm,
}: ReceivePurchaseOrderDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dateLocale = appDateLocale(i18n.language);
  const [dateValue, setDateValue] = useState(todayIsoDate);
  const [qtyValue, setQtyValue] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDateValue(todayIsoDate());
    setQtyValue(pendingQty != null && Number.isFinite(pendingQty) ? String(pendingQty) : '');
    setLocalError(null);
  }, [open, pendingQty]);

  if (!open) return null;

  const displayError = localError || error;

  const resolveDateOrError = (): string | null => {
    const date = dateValue.trim();
    if (!date) {
      setLocalError(t('supply.receivePo.dateRequired'));
      return null;
    }
    return date;
  };

  const handleConfirm = () => {
    if (busy) return;
    const date = resolveDateOrError();
    if (!date) return;
    let received_quantity: number | null = null;
    if (showQuantity) {
      const raw = String(qtyValue).trim().replace(',', '.');
      if (raw === '') {
        setLocalError(t('supply.receivePo.qtyRequired'));
        return;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        setLocalError(t('supply.receivePo.qtyRequired'));
        return;
      }
      if (pendingQty != null && n > pendingQty + 1e-9) {
        setLocalError(t('supply.receivePo.qtyExceeds', { pending: formatQty(pendingQty, locale) }));
        return;
      }
      received_quantity = n;
    }
    setLocalError(null);
    onConfirm({ received_date: date, received_quantity });
  };

  const handleCancelRemaining = () => {
    if (busy || !showQuantity) return;
    const date = resolveDateOrError();
    if (!date) return;
    const ok = window.confirm(t('supply.receivePo.cancelRemainingConfirm'));
    if (!ok) return;
    setLocalError(null);
    onConfirm({ received_date: date, received_quantity: 0 });
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={busy ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="receive-po-dialog-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="receive-po-dialog-title" className="text-base font-semibold text-gray-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{t('supply.receivePo.hint')}</p>
          </div>
          {!busy ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="space-y-4 px-5 py-4">
          {showQuantity ? (
            <div className="grid grid-cols-3 gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <div>
                <p className="text-gray-500">{t('supply.receivePo.ordered')}</p>
                <p className="mt-0.5 font-medium tabular-nums text-gray-900">{formatQty(orderedQty, locale)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('supply.receivePo.already')}</p>
                <p className="mt-0.5 font-medium tabular-nums text-gray-900">{formatQty(alreadyReceived, locale)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('supply.receivePo.pending')}</p>
                <p className="mt-0.5 font-medium tabular-nums text-amber-800">{formatQty(pendingQty, locale)}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {t('supply.receivePo.remainingAll', { count: lineCount ?? 0 })}
            </p>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('supply.receivePo.date')}</span>
            <div className="relative">
              <input
                type="date"
                lang={dateLocale}
                value={dateValue}
                disabled={busy}
                onChange={(e) => setDateValue(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50"
              />
              <span
                aria-hidden
                className={`pointer-events-none absolute inset-y-[1px] left-[1px] right-8 flex items-center rounded-md px-3 text-sm tabular-nums ${
                  busy ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'
                }`}
              >
                {formatIsoDateForLocale(dateValue, dateLocale)}
              </span>
            </div>
          </label>

          {showQuantity ? (
            <div>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">{t('supply.receivePo.qty')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={qtyValue}
                  disabled={busy}
                  onChange={(e) => setQtyValue(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50"
                />
              </label>
              <p className="mt-1 text-xs text-gray-500">{t('supply.receivePo.qtyHelp')}</p>
            </div>
          ) : null}

          {displayError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{displayError}</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {showQuantity ? (
              <button
                type="button"
                onClick={handleCancelRemaining}
                disabled={busy}
                title={t('supply.receivePo.cancelRemainingHint')}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
              >
                {t('supply.receivePo.cancelRemaining')}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('supply.receivePo.dismiss')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? t('supply.receivePo.saving') : t('supply.receivePo.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
